import { IconAlertTriangle, IconArrowsSplit2 } from "@tabler/icons-react";
import { useClavesSat } from "@/features/catalogos/hooks/use-catalogos";
import { useDividirConcepto } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import type { LineaProrrateo } from "@/features/prorrateo/types";
import { agruparPorConcepto, type ConceptoGrupo } from "@/features/prorrateo/utils/conceptos";
import { SolicitarAltaCatalogo } from "@/features/solicitudes-catalogo/components/SolicitarAltaCatalogo";
import {
  Alert, Badge, Button, Card, Divider, Group, Heading, Stack, Table, Tbody, Td, Text, Th, Thead, Tr,
} from "@/components/ui";
import { SubLineaRow } from "./SubLineaRow";

interface Props {
  lineas: LineaProrrateo[];
  disabled: boolean;
  solicitudId: string;
  numeroSolicitud: number;
  /** Slot del CFDI (Múltiples CFDI) · omitir para CFDI único. */
  numeroCfdi?: number;
  /** Oculta el header/aviso y el export (cuando la tabla vive dentro del card de un slot). */
  compacto?: boolean;
}

/**
 * Tabla del prorrateo por CONCEPTOS. Cada concepto (importe inmutable del CFDI/Invoice) se
 * muestra como un grupo: un encabezado con su clave/descripción, el importe objetivo, la suma
 * actual de sus sub-líneas y el botón "Dividir"; debajo, sus sub-líneas editables. El usuario
 * reparte el importe del concepto entre las sub-líneas (deben sumar el objetivo).
 */
export function ConceptosTable({ lineas, disabled, solicitudId, numeroSolicitud, numeroCfdi, compacto }: Props) {
  const { data: claves } = useClavesSat();
  const grupos = agruparPorConcepto(lineas);

  const descripcionDeClave = (clave: string) => claves.find((c) => c.clave === clave)?.descripcion;

  // Claves sin cuenta mapeada (dbo.Tag) · cada una origina UNA solicitud de catálogo.
  const clavesSinCuenta = Array.from(
    new Map(lineas.filter((l) => !l.cuentaContableCodigo).map((l) => [l.claveSat, l])).values()
  );

  const contenido = (
    <div className="flex flex-col gap-4">
      {!compacto && (
        <>
          <div>
            <Heading order={5}>Conceptos</Heading>
            <Text size="sm" c="dimmed" className="mt-0.5">
              Cada concepto viene del CFDI/factura y su importe no cambia. Puedes <strong>dividirlo</strong> en
              varias sub-líneas (con distinto centro de costo o cuenta) que deben sumar el importe del concepto.
            </Text>
          </div>
          <Divider />
        </>
      )}

        {!disabled && clavesSinCuenta.length > 0 && (
          <Alert variant="warning" icon={<IconAlertTriangle size={16} stroke={1.7} />}>
            <Stack gap="sm">
              <Text size="sm">
                {clavesSinCuenta.length === 1
                  ? "Una clave del CFDI no tiene cuenta contable mapeada."
                  : `${clavesSinCuenta.length} claves del CFDI no tienen cuenta contable mapeada.`}{" "}
                Puedes elegir la cuenta manualmente en la sub-línea, o solicitar el alta del mapeo al administrador:
              </Text>
              {clavesSinCuenta.map((l) => {
                const desc = descripcionDeClave(l.claveSat);
                return (
                  <div key={l.claveSat} className="flex flex-col gap-1">
                    <Text size="sm" fw={600} ff="mono">
                      {l.claveSat}
                      {desc && (
                        <Text span c="dimmed" fw={400}>
                          {" "}· {desc}
                        </Text>
                      )}
                    </Text>
                    <SolicitarAltaCatalogo
                      tipo="CuentaContablePorClave"
                      clave={l.claveSat}
                      descripcion={desc}
                      solicitudPagoId={solicitudId}
                      numeroSolicitudPago={numeroSolicitud}
                    />
                  </div>
                );
              })}
            </Stack>
          </Alert>
        )}

        <div className="overflow-x-auto">
          <Table verticalSpacing="sm" horizontalSpacing="xs">
            <Thead>
              <Tr>
                <Th className="w-10">#</Th>
                <Th>Centro de costo</Th>
                <Th>Cuenta</Th>
                <Th align="right">Importe</Th>
                <Th>Indicador</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {grupos.map((g) => (
                <ConceptoGrupoRows
                  key={g.conceptoNumero}
                  grupo={g}
                  disabled={disabled}
                  solicitudId={solicitudId}
                  numeroCfdi={numeroCfdi}
                  descripcionClave={descripcionDeClave(g.claveSat)}
                />
              ))}
            </Tbody>
          </Table>
        </div>
    </div>
  );

  return compacto ? contenido : <Card padding="lg">{contenido}</Card>;
}

function ConceptoGrupoRows({
  grupo,
  disabled,
  solicitudId,
  numeroCfdi,
  descripcionClave,
}: {
  grupo: ConceptoGrupo;
  disabled: boolean;
  solicitudId: string;
  numeroCfdi?: number;
  descripcionClave?: string;
}) {
  const dividir = useDividirConcepto(solicitudId, numeroCfdi);

  const tono = grupo.tieneCero ? "advertencia" : grupo.cuadra ? "exito" : "peligro";
  const estado = grupo.tieneCero
    ? "sub-línea en 0"
    : grupo.cuadra
      ? "cuadra"
      : grupo.diferencia > 0
        ? `sobran $${grupo.diferencia.toFixed(2)}`
        : `faltan $${(-grupo.diferencia).toFixed(2)}`;

  return (
    <>
      <Tr className="border-t border-border bg-muted/40">
        <Td colSpan={6}>
          <Group justify="space-between" align="center" wrap="nowrap" className="py-0.5">
            <div className="min-w-0">
              <Group gap="xs" wrap="nowrap">
                <Badge variant="light" color="slate">
                  Concepto {grupo.conceptoNumero}
                </Badge>
                <Text size="sm" fw={600} ff="mono" className="truncate">
                  {grupo.claveSat}
                </Text>
                {descripcionClave && (
                  <Text size="xs" c="dimmed" className="truncate">
                    · {descripcionClave}
                  </Text>
                )}
              </Group>
              {grupo.descripcion && (
                <Text size="xs" c="dimmed" className="mt-0.5 truncate">
                  {grupo.descripcion}
                </Text>
              )}
            </div>
            <Group gap="md" wrap="nowrap" align="center">
              <div className="text-right">
                <Text size="xs" c="dimmed" className="uppercase tracking-wide">
                  Objetivo
                </Text>
                <Text size="sm" fw={700}>
                  ${grupo.importeConcepto.toFixed(2)}
                </Text>
              </div>
              <div className="text-right">
                <Text size="xs" c="dimmed" className="uppercase tracking-wide">
                  Suma
                </Text>
                <Text size="sm" fw={600}>
                  ${grupo.sumaActual.toFixed(2)}
                </Text>
              </div>
              <Badge variant="light" color={tono} uppercase={false}>
                {estado}
              </Badge>
              {!disabled && (
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconArrowsSplit2 size={14} />}
                  loading={dividir.isPending}
                  onClick={() => dividir.mutate(grupo.conceptoNumero)}
                >
                  Dividir
                </Button>
              )}
            </Group>
          </Group>
        </Td>
      </Tr>
      {grupo.sublineas.map((l) => (
        <SubLineaRow
          key={l.id}
          linea={l}
          disabled={disabled}
          solicitudId={solicitudId}
          numeroCfdi={numeroCfdi}
          puedeEliminar={grupo.sublineas.length > 1}
        />
      ))}
    </>
  );
}
